import React, { useState, useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { DistributionTab, distributionPath, parseAppLocation } from '../routing/appRoutes';
import { ignorePlainClick } from '../routing/linkClick';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  TextField,
  Grid,
  Chip,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { NotebooksContext, FrontendNotebook, ArticleImage } from '../contexts/NotebooksContext';
import { format } from 'date-fns';
import { ArticleImageSearch } from './images';
import { ArticlePublishPanel } from './distribution/ArticlePublishPanel';
import {
  articleBodyTextForStats,
  draftListPreviewSnippet,
  plainTextStats,
  portalArticleDisplayParts,
} from '../utils/draftTextStats';

interface DistributionHubProps {
}

type PublicationTab = 'ready_to_publish' | 'scheduled' | 'published';

interface PublishingChannels {
  wordpress: boolean;
  medium: boolean;
  newsletter: boolean;
  ghost: boolean;
}

const DISTRIBUTION_TAB_ITEMS: { value: PublicationTab; tab: DistributionTab; label: string }[] = [
  { value: 'ready_to_publish', tab: 'ready', label: 'Ready to Publish' },
  { value: 'scheduled', tab: 'scheduled', label: 'Scheduled' },
  { value: 'published', tab: 'published', label: 'Published' },
];

export const DistributionHub: React.FC<DistributionHubProps> = () => {
  const { notebooks, schedulePublication, updateNotebook } = useContext(NotebooksContext);
  // Tab comes from the URL: /distribution, /distribution/scheduled, /distribution/published.
  const location = useLocation();
  const routeTab = (parseAppLocation(location.pathname)?.section ?? 'ready') as DistributionTab;
  const selectedTab: PublicationTab =
    DISTRIBUTION_TAB_ITEMS.find((t) => t.tab === routeTab)?.value ?? 'ready_to_publish';
  const [selectedArticle, setSelectedArticle] = useState<FrontendNotebook | null>(null);
  const [publishingChannels, setPublishingChannels] = useState<PublishingChannels>({
    wordpress: false,
    medium: false,
    newsletter: false,
    ghost: false,
  });
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [selectedImages, setSelectedImages] = useState<ArticleImage[]>([]);

  // Filter notebooks based on publication status
  const getFilteredNotebooks = (): FrontendNotebook[] => {
    return notebooks.filter(notebook => {
      if (!notebook.draft) return false;
      
      switch (selectedTab) {
        case 'ready_to_publish':
          return notebook.draft.ready_for_publishing && 
                 (!notebook.draft.publication_status || notebook.draft.publication_status === 'ready_to_publish');
        case 'scheduled':
          return notebook.draft.publication_status === 'scheduled';
        case 'published':
          return notebook.draft.publication_status === 'published';
        default:
          return false;
      }
    });
  };

  const filteredNotebooks = getFilteredNotebooks();

  const handleArticleClick = (notebook: FrontendNotebook) => {
    setSelectedArticle(notebook);
    // Reset publishing options
    setPublishingChannels({
      wordpress: false,
      medium: false,
      newsletter: false,
      ghost: false,
    });
    setScheduledDate('');
    setScheduledTime('');
    // Load existing images from draft
    setSelectedImages(notebook.draft?.images || []);
  };

  const handleCloseArticleView = () => {
    setSelectedArticle(null);
    setSelectedImages([]);
  };

  /**
   * Handle images change - persist to backend
   */
  const handleImagesChange = async (images: ArticleImage[]) => {
    if (!selectedArticle || !selectedArticle.draft) return;

    // Update local state immediately for responsive UI
    setSelectedImages(images);

    // Update the selected article's draft with new images
    const updatedDraft = {
      ...selectedArticle.draft,
      images,
    };

    try {
      // Persist to backend via updateNotebook
      await updateNotebook(selectedArticle.id, {
        draft: updatedDraft,
      } as Partial<FrontendNotebook>);

      // Update local selected article state
      setSelectedArticle({
        ...selectedArticle,
        draft: updatedDraft,
      });
    } catch (error) {
      console.error('Error saving images:', error);
      // Optionally revert on error
    }
  };

  const handleChannelToggle = (channel: keyof PublishingChannels) => {
    setPublishingChannels(prev => ({
      ...prev,
      [channel]: !prev[channel]
    }));
  };

  const handlePublishNow = () => {
    // TODO: Implement publish now logic
    console.log('Publishing now to:', publishingChannels);
    handleCloseArticleView();
  };

  const handleSchedulePublication = async () => {
    if (!selectedArticle) return;
    
    try {
      const success = await schedulePublication(selectedArticle.id, scheduledDate, scheduledTime);
      
      if (success) {
        console.log('Scheduled publication for:', scheduledDate, scheduledTime, 'to:', publishingChannels);
        handleCloseArticleView();
      }
    } catch (error) {
      console.error('Error scheduling publication:', error);
    }
  };

  const isScheduleValid = scheduledDate && scheduledTime;

  const formatDate = (timestamp: number) => {
    return format(new Date(timestamp * 1000), 'MMM d, yyyy h:mm a');
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid #27272a' }}>
        {/* Tabs */}
        <Tabs
          value={selectedTab}
          sx={{
            '& .MuiTab-root': {
              color: '#a1a1aa',
              textTransform: 'none',
              fontSize: '1rem',
              '&.Mui-selected': {
                color: '#60a5fa'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#60a5fa'
            }
          }}
        >
          {/* Router links (no Tabs onChange): modified clicks open a new tab without switching this one. */}
          {DISTRIBUTION_TAB_ITEMS.map(({ value, tab, label }) => (
            <Tab
              key={value}
              label={label}
              value={value}
              component={RouterLink}
              to={distributionPath(tab)}
              onClick={value === selectedTab ? ignorePlainClick : undefined}
            />
          ))}
        </Tabs>
      </Box>

      {/* Content List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {filteredNotebooks.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: '#a1a1aa'
          }}>
            <ArchiveIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No articles {selectedTab.replaceAll('_', ' ')}</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredNotebooks.map((notebook) => (
              <Grid item xs={12} key={notebook.id}>
                <Card
                  sx={{
                    bgcolor: '#1a1a1a',
                    border: '1px solid #27272a',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#60a5fa',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleArticleClick(notebook)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" gap={2}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '12px',
                          bgcolor: '#27272a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <ArticleIcon sx={{ color: '#60a5fa' }} />
                      </Box>

                      <Box flex={1}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                            {notebook.name}
                          </Typography>
                          <Chip
                            label="Article"
                            size="small"
                            sx={{
                              bgcolor: '#27272a',
                              color: '#60a5fa',
                              fontSize: '0.75rem',
                              height: 24
                            }}
                          />
                        </Box>

                        <Typography variant="h6" sx={{ color: '#fafafa', mb: 1, fontWeight: 600 }}>
                          {notebook.draft?.title || 'Untitled Article'}
                        </Typography>

                        {notebook.draft?.content && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#a1a1aa',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {draftListPreviewSnippet(notebook.draft.content)}
                          </Typography>
                        )}
                      </Box>

                      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                        {selectedTab === 'scheduled' && notebook.draft?.scheduled_publish_at && (
                          <>
                            <Chip
                              label="Scheduled"
                              size="small"
                              sx={{
                                bgcolor: '#f59e0b',
                                color: '#ffffff',
                                fontWeight: 500
                              }}
                            />
                            <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: 500 }}>
                              📅 {formatDate(notebook.draft.scheduled_publish_at)}
                            </Typography>
                          </>
                        )}
                        {selectedTab === 'published' && (
                          <Chip
                            label="Published"
                            size="small"
                            sx={{
                              bgcolor: '#60a5fa',
                              color: '#ffffff',
                              fontWeight: 500
                            }}
                          />
                        )}
                        <Typography variant="caption" sx={{ color: '#71717a' }}>
                          {notebook.draft?.created_at && `Created: ${formatDate(notebook.draft.created_at)}`}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            bgcolor: '#60a5fa',
                            color: '#0a0a0a',
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#3b82f6' }
                          }}
                        >
                          Publish
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Article Detail Dialog */}
      {selectedArticle && (
        <Dialog
          open={true}
          onClose={handleCloseArticleView}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#0a0a0a',
              border: '1px solid #27272a',
              height: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: '#0a0a0a', borderBottom: '1px solid #27272a', p: 3 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <ArticleIcon sx={{ color: '#60a5fa' }} />
              <Box>
                <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>
                  {selectedArticle.draft?.title || 'Untitled Article'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  {selectedArticle.name} • Article
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ bgcolor: '#0a0a0a', p: 3 }}>
            <Grid container spacing={3}>
              {/* Left Column - Preview */}
              <Grid item xs={12} md={7}>
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: '#fafafa' }}>
                  Article
                </Typography>
                <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #27272a', mb: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ color: '#fafafa', mb: 2, fontWeight: 700 }}>
                      {selectedArticle.draft?.title || 'Untitled Article'}
                    </Typography>
                    {(() => {
                      const raw = selectedArticle.draft?.content || '';
                      const parts = portalArticleDisplayParts(raw);
                      if (parts.isPortalArticle) {
                        return (
                          <>
                            {parts.excerpt ? (
                              <Typography
                                variant="subtitle1"
                                sx={{ color: '#a1a1aa', mb: 2, fontStyle: 'italic', lineHeight: 1.6 }}
                              >
                                {parts.excerpt}
                              </Typography>
                            ) : null}
                            <Typography
                              variant="body1"
                              sx={{
                                color: '#d4d4d8',
                                lineHeight: 1.8,
                                whiteSpace: 'pre-wrap',
                              }}
                            >
                              {parts.body.trim() || 'No article body yet.'}
                            </Typography>
                          </>
                        );
                      }
                      return (
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#d4d4d8',
                            lineHeight: 1.8,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {raw.trim() || 'No content available.'}
                        </Typography>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Statistics & Publishing */}
              <Grid item xs={12} md={5}>
                {/* Content Statistics */}
                <Typography variant="h6" sx={{ mt: 3, mb: 2, color: '#fafafa' }}>
                  Content Statistics
                </Typography>
                <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #27272a', p: 2.5, mb: 3 }}>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                        <Typography sx={{ fontSize: '1.2rem' }}>📝</Typography>
                        <Typography variant="caption" sx={{ color: '#a1a1aa', fontWeight: 500 }}>
                          Words
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>
                        {plainTextStats(articleBodyTextForStats(selectedArticle.draft?.content || '')).words.toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                        <Typography sx={{ fontSize: '1.2rem' }}>#️⃣</Typography>
                        <Typography variant="caption" sx={{ color: '#a1a1aa', fontWeight: 500 }}>
                          Characters
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>
                        {plainTextStats(articleBodyTextForStats(selectedArticle.draft?.content || '')).characters.toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                        <Typography sx={{ fontSize: '1.2rem' }}>⏰</Typography>
                        <Typography variant="caption" sx={{ color: '#a1a1aa', fontWeight: 500 }}>
                          Reading Time
                        </Typography>
                      </Box>
                      <Typography variant="h5" sx={{ color: '#fafafa', fontWeight: 700 }}>
                        {plainTextStats(articleBodyTextForStats(selectedArticle.draft?.content || '')).readingTimeMinutes} min
                      </Typography>
                    </Box>
                  </Box>
                </Card>

                {/* Publishing Channels */}
                <Typography variant="h6" sx={{ mb: 2, color: '#fafafa' }}>
                  Publishing Channels
                </Typography>
                <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #27272a', p: 2, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={publishingChannels.wordpress}
                        onChange={() => handleChannelToggle('wordpress')}
                        sx={{ color: '#60a5fa', '&.Mui-checked': { color: '#60a5fa' } }}
                      />
                    }
                    label={<Typography sx={{ color: '#fafafa' }}>WordPress</Typography>}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={publishingChannels.medium}
                        onChange={() => handleChannelToggle('medium')}
                        sx={{ color: '#60a5fa', '&.Mui-checked': { color: '#60a5fa' } }}
                      />
                    }
                    label={<Typography sx={{ color: '#fafafa' }}>Medium</Typography>}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={publishingChannels.newsletter}
                        onChange={() => handleChannelToggle('newsletter')}
                        sx={{ color: '#60a5fa', '&.Mui-checked': { color: '#60a5fa' } }}
                      />
                    }
                    label={<Typography sx={{ color: '#fafafa' }}>Newsletter</Typography>}
                    sx={{ mb: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={publishingChannels.ghost}
                        onChange={() => handleChannelToggle('ghost')}
                        sx={{ color: '#60a5fa', '&.Mui-checked': { color: '#60a5fa' } }}
                      />
                    }
                    label={<Typography sx={{ color: '#fafafa' }}>Ghost</Typography>}
                  />
                </Card>

                {/* Schedule Publication */}
                <Typography variant="h6" sx={{ mb: 2, color: '#fafafa' }}>
                  Schedule Publication (Optional)
                </Typography>
                <Card sx={{ bgcolor: '#1a1a1a', border: '1px solid #27272a', p: 2, mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 2 }}>
                    Leave empty to publish immediately, or select a date and time to schedule:
                  </Typography>
                  <TextField
                    label="Date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    fullWidth
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: '#fafafa',
                        '& fieldset': { borderColor: '#3f3f46' },
                        '&:hover fieldset': { borderColor: '#a1a1aa' },
                        '&.Mui-focused fieldset': { borderColor: '#60a5fa' }
                      },
                      '& .MuiInputLabel-root': {
                        color: '#a1a1aa'
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fafafa',
                        '& fieldset': { borderColor: '#3f3f46' },
                        '&:hover fieldset': { borderColor: '#a1a1aa' },
                        '&.Mui-focused fieldset': { borderColor: '#60a5fa' }
                      },
                      '& .MuiInputLabel-root': {
                        color: '#a1a1aa'
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Card>
              </Grid>

              {/* Full-width Image Search Section */}
              <Grid item xs={12}>
                <ArticleImageSearch
                  selectedImages={selectedImages}
                  onImagesChange={handleImagesChange}
                  maxImages={5}
                />
              </Grid>
            </Grid>
            <ArticlePublishPanel
              notebookId={selectedArticle.id}
              pieceTitle={selectedArticle.draft?.title || selectedArticle.name}
            />
          </DialogContent>

          <DialogActions sx={{ bgcolor: '#0a0a0a', borderTop: '1px solid #27272a', p: 3, justifyContent: 'space-between' }}>
            <Box display="flex" gap={2}>
              <Button
                variant="contained"
                onClick={handlePublishNow}
                disabled={!Object.values(publishingChannels).some(v => v)}
                sx={{
                  bgcolor: '#60a5fa',
                  color: '#0a0a0a',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#3b82f6' },
                  '&.Mui-disabled': {
                    bgcolor: '#27272a',
                    color: '#52525b'
                  }
                }}
              >
                Publish Now
              </Button>
              <Button
                variant="outlined"
                onClick={handleSchedulePublication}
                disabled={!isScheduleValid || !Object.values(publishingChannels).some(v => v)}
                sx={{
                  borderColor: '#3f3f46',
                  color: '#fafafa',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#60a5fa',
                    bgcolor: '#27272a'
                  },
                  '&.Mui-disabled': {
                    borderColor: '#27272a',
                    color: '#52525b'
                  }
                }}
              >
                Schedule for Later
              </Button>
            </Box>
            <Button
              onClick={handleCloseArticleView}
              sx={{ color: '#a1a1aa', textTransform: 'none' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

